import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

export const CountdownTimer: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // 倒计时数字序列（5 → 4 → 3 → 2 → 1 → GO!）
  const countdownSequence = [5, 4, 3, 2, 1, 'GO!'];
  const framesPerNumber = durationInFrames / countdownSequence.length;
  const currentIndex = Math.floor(frame / framesPerNumber);
  const currentNumber = countdownSequence[currentIndex];

  // 当前数字的进度
  const numberProgress = (frame % framesPerNumber) / framesPerNumber;

  // 弹跳缩放效果
  const scale = spring({
    frame: frame % framesPerNumber,
    fps,
    config: {
      damping: 12,
      stiffness: 200,
      mass: 0.5,
    },
  });

  // 淡出效果
  const opacity = interpolate(
    numberProgress,
    [0.7, 1],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // 旋转效果
  const rotation = interpolate(numberProgress, [0, 1], [0, 360]);

  // 背景圆环脉冲
  const ringScale = interpolate(numberProgress, [0, 1], [1, 1.5], {
    extrapolateRight: 'clamp',
  });
  const ringOpacity = interpolate(numberProgress, [0, 1], [0.8, 0], {
    extrapolateRight: 'clamp',
  });

  // 颜色渐变（基于数字）
  const hue = interpolate(
    currentIndex,
    [0, countdownSequence.length - 1],
    [0, 120]
  );
  const color = `hsl(${hue}, 80%, 60%)`;
  const glowColor = `hsl(${hue}, 100%, 70%)`;

  // 粒子装饰
  const particles = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2 + frame * 0.02;
    const distance = interpolate(numberProgress, [0, 1], [120, 180]);
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;
    const size = interpolate(numberProgress, [0, 1], [8, 4]);
    const particleOpacity = interpolate(numberProgress, [0.5, 1], [1, 0]);

    return { x, y, size, opacity: particleOpacity, angle: i };
  });

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(circle, #1a1a2e 0%, #16213e 100%)',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 背景圆环 */}
      <div
        style={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          border: `4px solid ${color}`,
          opacity: ringOpacity,
          transform: `scale(${ringScale})`,
          boxShadow: `0 0 40px ${glowColor}`,
        }}
      />

      {/* 粒子装饰 */}
      {particles.map((particle, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            background: color,
            left: '50%',
            top: '50%',
            transform: `translate(${particle.x}px, ${particle.y}px)`,
            opacity: particle.opacity,
            boxShadow: `0 0 10px ${glowColor}`,
          }}
        />
      ))}

      {/* 主数字 */}
      <div
        style={{
          fontSize: 200,
          fontWeight: 'bold',
          color: color,
          opacity,
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          textShadow: `
            0 0 20px ${glowColor},
            0 0 40px ${glowColor},
            0 0 60px ${glowColor}
          `,
        }}
      >
        {currentNumber}
      </div>

      {/* 进度指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          display: 'flex',
          gap: 10,
        }}
      >
        {countdownSequence.map((_, i) => (
          <div
            key={i}
            style={{
              width: 40,
              height: 4,
              background:
                i <= currentIndex
                  ? color
                  : 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>

      {/* 标题文字 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.8)',
          letterSpacing: 4,
          textTransform: 'uppercase',
        }}
      >
        准备开始
      </div>
    </AbsoluteFill>
  );
};
